import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Loader2 } from 'lucide-react';
import Modal from '../ui/Modal';
import { supabase, retryOperation } from '../../lib/supabase';
import Logo from '../Logo';

interface LoginPromptModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function LoginPromptModal({ isOpen, onClose }: LoginPromptModalProps) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Create unauth user first to track the email
      const { data: userId, error: userError } = await retryOperation(() =>
        supabase.rpc('handle_unauth_user', {
          user_email: email.toLowerCase()
        })
      );

      if (userError) throw userError;

      // Send magic link
      const { error: magicLinkError } = await supabase.auth.signInWithOtp({
        email: email.toLowerCase(),
        options: {
          emailRedirectTo: `${window.location.origin}/magic-link-callback`,
        }
      });

      if (magicLinkError) throw magicLinkError;

      // Submit to Netlify Forms for newsletter subscription
      const formData = new FormData();
      formData.append('form-name', 'newsletter');
      formData.append('email', email);

      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as any).toString()
      });

      if (!response.ok) {
        console.warn('Failed to submit newsletter form, but continuing');
      }
      
      setSubmitted(true);
      setTimeout(() => {
        onClose();
      }, 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="">
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <Logo size="lg" showText={false} />
        </div>
        
        <h3 className="text-xl font-semibold mb-2">
         Well, Hey There!
<br></br>
          Let's Grow Together
        </h3>
        
        {submitted ? (
          <div className="space-y-4">
            <p className="text-green-600 font-medium">Magic link sent!</p>
            <p className="text-gray-600">
              We've sent a magic link to your email. Click the link to sign in and access your personalized garden recommendations.
            </p>
          </div>
        ) : (
          <>
            <p className="text-gray-600 mb-6">
              Enter your email to get the Free 5-Minute Garden Starter Kit Download.
            </p>

            <form 
              onSubmit={handleSubmit} 
              className="max-w-sm mx-auto"
              name="newsletter"
              method="POST"
              data-netlify="true"
            >
              {/* Hidden input for Netlify Forms */}
              <input type="hidden" name="form-name" value="newsletter" />
              
              <div className="relative mb-4">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                />
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              </div>

              {error && (
                <p className="text-sm text-red-600 mb-4">{error}</p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-primary-500 text-white py-2 px-4 rounded-md hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  'Submit'
                )}
              </button>
            </form>

            <p className="text-xs text-gray-500 mt-4">
              You’ll also get quick, seasonal tips to help you grow more in less time. Unsubscribe anytime.
            </p>
          </>
        )}
      </div>
    </Modal>
  );
}